import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { AuthGuard } from '@usoftware/auth-react'
import { Sidebar, useSidebarState } from './sidebar'
import { TopNav } from './top-nav'
import { MobileNav } from './mobile-nav'
import { Skeleton } from '@/components/ui'

interface AppShellProps {
  children?: React.ReactNode
}

function LoadingState() {
  return (
    <div className="flex h-screen bg-[var(--bg-base)]">
      <div className="hidden lg:flex w-64 flex-col border-r border-[var(--border-default)] bg-[var(--bg-elevated)]">
        <div className="p-4">
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="flex-1 p-4 space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="h-16 border-b border-[var(--border-default)] bg-[var(--bg-elevated)] flex items-center px-4">
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    </div>
  )
}

export function AppShell({ children }: AppShellProps) {
  const { collapsed, toggle } = useSidebarState()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    <AuthGuard
      redirectToLogin
      loading={<LoadingState />}
    >
      <div className="flex h-screen bg-[var(--bg-base)]">
        {/* Sidebar - hidden on mobile */}
        <Sidebar
          collapsed={collapsed}
          onToggle={toggle}
          className="hidden lg:flex"
        />

        {/* Mobile navigation */}
        <MobileNav
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopNav
            showMenuButton
            onMenuClick={() => setMobileNavOpen(true)}
          />

          <main className="flex-1 overflow-auto">
            <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
              {children || <Outlet />}
            </div>
          </main>
        </div>
      </div>
    </AuthGuard>
  )
}
